import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertTriangle } from 'lucide-react';
import { useApproveTransfer, useHQApproveTransfer, type ApproveTransferInput } from '../../../api/mutations/transfer.mutations';
import type { Transfer } from '../../../api/queries/transfer.queries';

interface ApproveTransferModalProps {
  transfer: Transfer;
  isHQApproval?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ApprovalItem {
  itemId: string;
  ingredientName: string;
  requestedQuantity: number;
  approvedQuantity: number;
  unit: string;
  overrideFEFO?: boolean;
  overrideReason?: string;
}

export default function ApproveTransferModal({
  transfer,
  isHQApproval = false,
  onClose,
  onSuccess,
}: ApproveTransferModalProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<ApprovalItem[]>(
    transfer.items.map((item) => {
      const ingredient = typeof item.ingredientId === 'object' ? item.ingredientId : null;
      return {
        itemId: item._id,
        ingredientName: ingredient?.name?.en || '-',
        requestedQuantity: item.requestedQuantity,
        approvedQuantity: item.requestedQuantity,
        unit: item.unit,
        overrideFEFO: false,
        overrideReason: '',
      };
    })
  );
  const [notes, setNotes] = useState('');
  const [expectedDeliveryHours, setExpectedDeliveryHours] = useState(24);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const approveTransfer = useApproveTransfer(transfer._id);
  const hqApproveTransfer = useHQApproveTransfer(transfer._id);

  const updateItem = (index: number, updates: Partial<ApprovalItem>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const hasQuantityChanges = items.some((item) => item.approvedQuantity !== item.requestedQuantity);
  const hasRejections = items.some((item) => item.approvedQuantity === 0);

  const handleApprove = async () => {
    try {
      if (isHQApproval) {
        await hqApproveTransfer.mutateAsync({
          action: 'approve',
          modifiedItems: items.map((item) => ({
            itemId: item.itemId,
            approvedQuantity: item.approvedQuantity,
          })),
        });
      } else {
        const payload: ApproveTransferInput = {
          items: items.map((item) => ({
            itemId: item.itemId,
            approvedQuantity: item.approvedQuantity,
            overrideFEFO: item.overrideFEFO,
            overrideReason: item.overrideFEFO ? item.overrideReason : undefined,
          })),
          notes: notes || undefined,
          expectedDeliveryHours,
        };
        await approveTransfer.mutateAsync(payload);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to approve transfer:', error);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert(t('transfers.rejectReasonRequired'));
      return;
    }

    try {
      if (isHQApproval) {
        await hqApproveTransfer.mutateAsync({
          action: 'reject',
          reason: rejectReason,
        });
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to reject transfer:', error);
    }
  };

  const fromLocation = typeof transfer.fromLocationId === 'object' ? transfer.fromLocationId : null;
  const toLocation = typeof transfer.toLocationId === 'object' ? transfer.toLocationId : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <h2 className="text-xl font-bold">
              {isHQApproval ? t('transfers.hqApproval') : t('transfers.approveTransfer')}
            </h2>
            <span className="text-sm text-muted-foreground">({transfer.transferNumber})</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Transfer Info */}
            <div className="p-4 rounded-lg bg-secondary/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">{t('transfers.from')}</p>
                  <p className="font-medium">{fromLocation?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('transfers.to')}</p>
                  <p className="font-medium">{toLocation?.name || '-'}</p>
                </div>
              </div>
            </div>

            {/* Warning for changes */}
            {hasQuantityChanges && (
              <div className="flex items-center gap-2 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800">
                <AlertTriangle className="h-5 w-5" />
                <p className="text-sm">{t('transfers.quantityChangesWarning')}</p>
              </div>
            )}

            {/* Items Table */}
            <div>
              <h3 className="text-sm font-medium mb-3">{t('transfers.itemsToApprove')}</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="text-left py-2 px-3">{t('transfers.ingredient')}</th>
                      <th className="text-right py-2 px-3">{t('transfers.requested')}</th>
                      <th className="text-right py-2 px-3 w-32">{t('transfers.approved')}</th>
                      {!isHQApproval && (
                        <th className="text-center py-2 px-3 w-32">{t('transfers.overrideFEFO')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const hasChange = item.approvedQuantity !== item.requestedQuantity;
                      return (
                        <tr key={item.itemId} className={`border-t ${hasChange ? 'bg-yellow-50' : ''}`}>
                          <td className="py-2 px-3">{item.ingredientName}</td>
                          <td className="py-2 px-3 text-right">
                            {item.requestedQuantity} {item.unit}
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="number"
                              value={item.approvedQuantity}
                              onChange={(e) =>
                                updateItem(index, {
                                  approvedQuantity: Math.max(0, parseFloat(e.target.value) || 0),
                                })
                              }
                              className={`w-full rounded border bg-background px-2 py-1 text-right ${
                                hasChange ? 'border-yellow-400' : ''
                              }`}
                              min={0}
                              max={item.requestedQuantity * 2}
                              step={0.1}
                            />
                          </td>
                          {!isHQApproval && (
                            <td className="py-2 px-3 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={item.overrideFEFO || false}
                                    onChange={(e) =>
                                      updateItem(index, { overrideFEFO: e.target.checked })
                                    }
                                    className="sr-only peer"
                                  />
                                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                                </label>
                                {item.overrideFEFO && (
                                  <input
                                    type="text"
                                    value={item.overrideReason || ''}
                                    onChange={(e) =>
                                      updateItem(index, { overrideReason: e.target.value })
                                    }
                                    placeholder={t('transfers.overrideReason')}
                                    className="w-full rounded border bg-background px-2 py-1 text-xs"
                                    required
                                  />
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Expected Delivery Hours (Warehouse approval only) */}
            {!isHQApproval && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('transfers.expectedDeliveryHours')}
                </label>
                <select
                  value={expectedDeliveryHours}
                  onChange={(e) => setExpectedDeliveryHours(parseInt(e.target.value))}
                  className="w-full max-w-xs rounded-lg border bg-background px-3 py-2"
                >
                  <option value={8}>8 {t('common.hours')}</option>
                  <option value={12}>12 {t('common.hours')}</option>
                  <option value={24}>24 {t('common.hours')}</option>
                  <option value={48}>48 {t('common.hours')}</option>
                  <option value={72}>72 {t('common.hours')}</option>
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2"
                rows={3}
                placeholder={t('transfers.approvalNotesPlaceholder')}
              />
            </div>

            {/* Reject Section */}
            {isHQApproval && showReject && (
              <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                <h3 className="text-sm font-medium text-red-800 mb-2">{t('transfers.rejectTransfer')}</h3>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2"
                  rows={3}
                  placeholder={t('transfers.rejectReasonPlaceholder')}
                  required
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between">
          <div>
            {isHQApproval && !showReject && (
              <button
                type="button"
                onClick={() => setShowReject(true)}
                className="px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
              >
                {t('transfers.rejectTransfer')}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
            >
              {t('common.cancel')}
            </button>
            {showReject ? (
              <button
                onClick={handleReject}
                disabled={hqApproveTransfer.isPending || !rejectReason.trim()}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {hqApproveTransfer.isPending ? t('common.saving') : t('transfers.confirmReject')}
              </button>
            ) : (
              <button
                onClick={handleApprove}
                disabled={approveTransfer.isPending || hqApproveTransfer.isPending || hasRejections}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {(approveTransfer.isPending || hqApproveTransfer.isPending)
                  ? t('common.saving')
                  : t('transfers.approveTransfer')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
